/**
 * Modal "ARCHIVADOS" — Fase 10a.3.
 *
 * Lista todos los NOTAMs archivados o caducados (vista admin+). Cada
 * fila ofrece:
 *  - RESTAURAR  → PATCH `archived=false` (solo si NO ha caducado;
 *                 los caducados ya no se pueden mostrar en el tablón).
 *  - BORRAR     → DELETE /api/notams/:id (definitivo).
 *
 * El modal se cierra solo con Cerrar / Escape / click backdrop; las
 * acciones de cada fila se manejan vía callbacks que devuelve el
 * caller (mantenemos la I/O fuera del modal para no acoplar este UI
 * a `api.ts`).
 *
 * Paginación (Fase 10c): el backend devuelve la lista completa, pero
 * el modal sólo renderiza `PAGE_SIZE` filas iniciales y muestra un
 * botón "CARGAR MÁS" al final. Cada click añade otro tramo. Es
 * suficiente hasta varios cientos de rows; si crece a miles habrá
 * que paginar también en backend.
 */

import type { Notam } from '@51esc/shared';

import './archived-modal.css';

export interface ArchivedModalActions {
  /** Restaurar (desarchivar) un NOTAM. Devuelve true si OK, false si error. */
  onRestore: (notamId: string) => Promise<boolean>;
  /** Borrar definitivamente. Devuelve true si OK. */
  onDelete: (notamId: string) => Promise<boolean>;
}

interface ModalRefs {
  dialog: HTMLDialogElement;
  list: HTMLElement;
  emptyMsg: HTMLElement;
  loadMoreBtn: HTMLButtonElement;
  loadMoreWrap: HTMLElement;
  closeBtn: HTMLButtonElement;
}

let refs: ModalRefs | null = null;

/** Tamaño de página del lazy-load. Ver doc del módulo. */
const PAGE_SIZE = 20;

/**
 * Abre el modal con la lista actual de archivados. Devuelve una promesa
 * que resuelve cuando el modal se cierra (sin valor de retorno — el
 * caller refresca el estado tras las acciones).
 */
export function openArchivedModal(
  archived: Notam[],
  actions: ArchivedModalActions,
): Promise<void> {
  const r = ensureModal();
  // Copia mutable del array para que restaurar/borrar pueda quitar
  // entradas y un rerender posterior (al pulsar "cargar más") no
  // las pinte de nuevo.
  const items: Notam[] = [...archived];
  // Estado de paginación local a este open: cuántas filas hay
  // renderizadas. Cada "cargar más" suma PAGE_SIZE.
  let visibleCount = Math.min(PAGE_SIZE, items.length);
  const now = new Date().toISOString();

  // Pinta filas en el rango [from, to) sin tocar las existentes.
  // Se usa para el render inicial y para "Cargar más" — el segundo
  // hace append en lugar de rerender completo para preservar el
  // scroll del usuario.
  const renderRange = (from: number, to: number): void => {
    for (let i = from; i < to; i++) {
      const n = items[i];
      if (!n) continue;
      const isExpired = n.expiresAt <= now;
      r.list.append(renderRow(n, isExpired, actions, onRowRemoved));
    }
  };

  const renderAllVisible = (): void => {
    r.list.innerHTML = '';
    if (items.length === 0) {
      r.emptyMsg.hidden = false;
      r.loadMoreWrap.hidden = true;
      return;
    }
    r.emptyMsg.hidden = true;
    if (visibleCount > items.length) visibleCount = items.length;
    renderRange(0, visibleCount);
    updateLoadMore();
  };

  const updateLoadMore = (): void => {
    const remaining = items.length - visibleCount;
    if (remaining <= 0) {
      r.loadMoreWrap.hidden = true;
      return;
    }
    r.loadMoreWrap.hidden = false;
    const step = Math.min(PAGE_SIZE, remaining);
    r.loadMoreBtn.textContent = `Cargar más (${step} de ${remaining} restantes)`;
  };

  const onLoadMore = (): void => {
    const oldVisible = visibleCount;
    visibleCount = Math.min(visibleCount + PAGE_SIZE, items.length);
    // Append-only: añadimos solo el tramo nuevo al final de la lista
    // en lugar de borrar y repintar todo. Así el scroll del usuario
    // se mantiene en la posición donde estaba.
    renderRange(oldVisible, visibleCount);
    updateLoadMore();
  };

  // Sincroniza el array al éxito de restaurar/borrar para que un
  // rerender futuro no muestre filas zombi.
  const onRowRemoved = (notamId: string): void => {
    const idx = items.findIndex((n) => n.id === notamId);
    if (idx >= 0) items.splice(idx, 1);
    if (visibleCount > items.length) visibleCount = items.length;
    if (items.length === 0) {
      r.emptyMsg.hidden = false;
    }
    updateLoadMore();
  };

  renderAllVisible();

  return new Promise<void>((resolve) => {
    let resolved = false;
    const finish = (): void => {
      if (resolved) return;
      resolved = true;
      r.dialog.close();
      r.closeBtn.removeEventListener('click', onClose);
      r.dialog.removeEventListener('cancel', onClose);
      r.dialog.removeEventListener('click', onBackdrop);
      r.loadMoreBtn.removeEventListener('click', onLoadMore);
      resolve();
    };
    const onClose = (ev?: Event): void => {
      ev?.preventDefault();
      finish();
    };
    const onBackdrop = (ev: MouseEvent): void => {
      if (ev.target === r.dialog) finish();
    };

    r.closeBtn.addEventListener('click', onClose);
    r.dialog.addEventListener('cancel', onClose);
    r.dialog.addEventListener('click', onBackdrop);
    r.loadMoreBtn.addEventListener('click', onLoadMore);

    r.dialog.showModal();
    r.closeBtn.focus();
  });
}

function renderRow(
  n: Notam,
  isExpired: boolean,
  actions: ArchivedModalActions,
  onRemoved: (notamId: string) => void,
): HTMLElement {
  const li = document.createElement('li');
  li.className = 'am-row';
  if (isExpired) li.classList.add('am-row--expired');
  li.dataset['notamId'] = n.id;

  const headerHtml = `
    <header class="am-row-meta">
      <span class="am-row-author">${escapeHtml(n.authorCallsign)}</span>
      <span class="am-row-date">${formatDate(n.publishedAt)}</span>
      ${isExpired ? '<span class="am-row-tag am-row-tag--expired">CADUCADO</span>' : ''}
      ${n.archived && !isExpired ? '<span class="am-row-tag am-row-tag--archived">ARCHIVADO</span>' : ''}
    </header>
  `;
  li.innerHTML = `
    ${headerHtml}
    <p class="am-row-body">${escapeHtml(n.body)}</p>
    <div class="am-row-actions">
      ${isExpired
        ? '<span class="am-row-hint">Caducados no se pueden restaurar.</span>'
        : '<button type="button" class="am-btn am-btn--restore">Restaurar</button>'}
      <button type="button" class="am-btn am-btn--delete">Borrar</button>
    </div>
  `;

  const restoreBtn = li.querySelector<HTMLButtonElement>('.am-btn--restore');
  restoreBtn?.addEventListener('click', () => {
    void runAction(li, restoreBtn, () => actions.onRestore(n.id), () =>
      onRemoved(n.id),
    );
  });
  const deleteBtn = li.querySelector<HTMLButtonElement>('.am-btn--delete');
  deleteBtn?.addEventListener('click', () => {
    void runAction(li, deleteBtn, () => actions.onDelete(n.id), () =>
      onRemoved(n.id),
    );
  });

  return li;
}

/**
 * Wrapper común: deshabilita el botón mientras la promesa está en
 * curso y, en éxito, retira la fila del DOM y avisa al caller para
 * que actualice el array. Si falla, el button vuelve a quedar
 * habilitado (el caller habrá mostrado el error).
 */
async function runAction(
  row: HTMLElement,
  btn: HTMLButtonElement,
  fn: () => Promise<boolean>,
  onSuccess: () => void,
): Promise<void> {
  btn.disabled = true;
  const ok = await fn();
  if (ok) {
    row.remove();
    onSuccess();
  } else {
    btn.disabled = false;
  }
}

// ─── Construcción del dialog ─────────────────────────────────────────

function ensureModal(): ModalRefs {
  if (refs !== null) return refs;
  const dialog = document.createElement('dialog');
  dialog.className = 'am-dialog';
  dialog.innerHTML = `
    <div class="am-card">
      <h2 class="am-title">ARCHIVADOS</h2>
      <p class="am-subtitle">
        Aquí caen los NOTAMs que has archivado y los que han caducado
        (5 días). Restaurar devuelve uno al tablón; los caducados solo
        se pueden borrar definitivamente.
      </p>
      <ul class="am-list"></ul>
      <p class="am-empty" hidden>— No hay NOTAMs archivados ni caducados. —</p>
      <div class="am-load-more" hidden>
        <button type="button" class="am-btn am-btn--load-more">Cargar más</button>
      </div>
      <div class="am-footer">
        <button type="button" class="am-btn am-close">Cerrar</button>
      </div>
    </div>
  `;
  document.body.appendChild(dialog);

  const get = <T extends HTMLElement>(sel: string): T => {
    const el = dialog.querySelector<T>(sel);
    if (!el) throw new Error(`[archived-modal] missing ${sel}`);
    return el;
  };
  refs = {
    dialog,
    list: get<HTMLElement>('.am-list'),
    emptyMsg: get<HTMLElement>('.am-empty'),
    loadMoreWrap: get<HTMLElement>('.am-load-more'),
    loadMoreBtn: get<HTMLButtonElement>('.am-btn--load-more'),
    closeBtn: get<HTMLButtonElement>('.am-close'),
  };
  return refs;
}

// ─── Util locales ────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
